/*!
 * Copyright 2017 - 2020 by ChartIQ, Inc.
 * All rights reserved.
 */

/*
 * Finsemble run-time JSON-schema functions
 */

import Logger from "../../clients/logger";
import { ConfigUtilInstance as ConfigUtil } from "../../common/configUtil";
import Ajv, { Options as AjvOptions } from "ajv";
import addFormats from "ajv-formats";

const EXCESSIVE_SCHEMA_DEPTH = 50;
const ARRAY_DELIMITER = "%&%";
const PATH_DELIMITER = "%%";
const INITIAL_LEVEL = 0;

class SchemaUtil {
	finsembleSchema: any;

	ajv: any;

	generatedDocLines = [];

	knownPropertiesMap = {};

	previousLevel = INITIAL_LEVEL;

	constructor() {}

	/**
	 * Reformat ajv errors to make a little more readable and intuitive
	 *
	 * @private
	 * @param {*} ajvErrors
	 * @returns new object for logging with formatted list of ajv errors
	 * @memberof SchemaUtil
	 */
	private formatValidationErrors(ajvErrors) {
		return ajvErrors.map(
			(err) => `In '${err.instancePath}', error: '${err.message}', with params '${JSON.stringify(err.params)}'`
		);
	}

	/**
	 * Lower level verification function, typically called only by validateConfig
	 *
	 * @param {*} ajv instance of ajv module used to do the verification
	 * @param {*} schema the schema for verifying
	 * @param {*} schemaDefinition a definition/key within schema to verify with
	 * @param {*} configToVerify object that contains the config to verify
	 * @returns true if validated successfully; false if errors
	 */
	public schemaValidate(ajv, schema, schemaDefinition, configToVerify) {
		let returnValue = false;
		try {
			// imesner: 20211103: upgrade ajv changes schema name / subschema path
			var validate = ajv.getSchema(`Finsemble${schemaDefinition}`);

			var valid = validate(configToVerify);
			if (!valid) {
				Logger.system.error(
					"forceObjectsToLogger",
					"JSON Schema Validation Errors",
					this.formatValidationErrors(validate.errors),
					"Details",
					validate.errors,
					"manifest",
					configToVerify,
					"schema",
					schema
				);
			} else {
				console.log("JSON Schema Validation: No Errors");
				Logger.system.log("JSON Schema Validation: Done With No Errors");
				returnValue = true;
			}
		} catch (err) {
			console.log("schemaValidate catch: ", err, schema, configToVerify);
			Logger.system.error("forceObjectsToLogger", "schemaValidate catch", err, schema, configToVerify);
		}

		return returnValue;
	}

	/**
	 * Validates config using JSON Schema.  If the schema hasn't been ready yet, then this function will read and cache it.
	 *
	 * @param {*} manifest object that contains Finsemble's manifest
	 * @param {*} schemaDefinition a definition within Finsemble's schema to verify with -- this allows verification on subschema. Example: "#/definitions/BootManifestSubschema".
	 * @returns error string or null if no error
	 */
	public async validateConfig(manifest, schemaDefinition): Promise<boolean> {
		console.log("JSON Schema Validation: validateConfig", schemaDefinition, manifest);
		Logger.system.debug("forceObjectsToLogger", "JSON Schema Validation: validateConfig", schemaDefinition, manifest);
		let schemaReady = false;

		let validationOptions: AjvOptions = {
			// imesner 20211103: strict mode enabled by default on ajv v8 results in many many errors.  Logging them
			strict: "log",
			allErrors: true,
			verbose: true,
			useDefaults: false,
			// we want useDefaults: false, otherwise AJV will conovert null config values to their defaults
			// even if "null" is specified as a valid type in the JSON schema for the config value
		};

		// first time will need to read the schema and cache it, plus initialize ajv
		if (!this.finsembleSchema || !this.ajv) {
			try {
				const { err, data } = await ConfigUtil.fetchConfigFile(manifest.finsemble.configSchemaURL);
				this.finsembleSchema = data;
				if (err) {
					Logger.system.error(`getFile error: ${err}`);
				}
				this.ajv = new Ajv(validationOptions);
				// imesner: 20211103: upgrade to ajv v8 from v3 uses strict mode and broke on custom keywords
				// and the uri-reference format
				addFormats(this.ajv);

				this.ajv.addSchema(this.finsembleSchema, "Finsemble");
				//require("ajv-keywords")(this.ajv); // <---- for now don't add advanced keywords
				schemaReady = true;
			} catch (thrownErr) {
				Logger.system.error(`validateConfig: Error thrown reading or adding schema: ${thrownErr}`);
			}
		} else {
			schemaReady = true;
		}

		let isError = false;
		if (!schemaReady) {
			Logger.system.error(
				"Failure reading config schema. Confirm the schema URL configured in finsemble.configSchemaURL is correct and the file contains a valid JSON schema."
			);
			isError = true;
		} else {
			Logger.system.debug("ConfigService: schema read", this.finsembleSchema);
			if (!this.schemaValidate(this.ajv, this.finsembleSchema, schemaDefinition, manifest)) {
				isError = true;
			}
		}
		return isError;
	}

	/**
	 * Set one config default value using schema default
	 *
	 * @private
	 * @param {*} manifest
	 * @param {*} path
	 * @param {*} defaultValue
	 * @memberof SchemaUtil
	 */
	private setDefaultForForOneProperty(manifest, path, defaultValue) {
		Logger.system.debug(`setDefaultForForOneProperty: ${path}=${defaultValue}`);
		let originalPropertyValue = null;
		let currentConfigPosition = manifest;
		let pathArgs = path.split(PATH_DELIMITER);
		for (let i = 1; i < pathArgs.length - 1; i++) {
			let currentProperty = pathArgs[i];

			if (currentProperty.includes(ARRAY_DELIMITER)) {
				let arrayTokens = currentProperty.split(ARRAY_DELIMITER);
				let arrayName = arrayTokens[0];
				let arrayIndex = arrayTokens[1];
				currentConfigPosition = currentConfigPosition[arrayName][arrayIndex];
			} else {
				if (!currentConfigPosition[currentProperty]) {
					currentConfigPosition[currentProperty] = {};
				}
				currentConfigPosition = currentConfigPosition[currentProperty];
			}
		}
		let propertyWithDefault = pathArgs[pathArgs.length - 1];

		originalPropertyValue = currentConfigPosition[propertyWithDefault];
		if (typeof currentConfigPosition === "object" && typeof originalPropertyValue === "undefined") {
			currentConfigPosition[propertyWithDefault] = defaultValue;
			Logger.system.debug(
				`config-default-set property: path=${path}, originalPropertyValue=${originalPropertyValue} defaultValue=${defaultValue},`
			);
		}
	}

	/**
	 * Returns which config properties correspond to "additional properties".  If a config property doesn't match explicitly defined property, but does match additional property scheme, then returned here.
	 *
	 * @private
	 * @param {*} manifest
	 * @param {*} path
	 * @returns {String[]}
	 * @memberof SchemaUtil
	 */
	private getAdditionalProperties(manifest, path): String[] {
		Logger.system.verbose(`getAdditionalProperties for ${path}`);
		let currentConfigPosition = manifest;

		let pathArgs = path.split(PATH_DELIMITER);
		for (let i = 1; i < pathArgs.length; i++) {
			let currentProperty = pathArgs[i];
			if (!currentConfigPosition[currentProperty]) {
				currentConfigPosition[currentProperty] = {};
			}
			currentConfigPosition = currentConfigPosition[currentProperty];
		}

		let currentProperties = Object.keys(currentConfigPosition);
		let additionalProperties: String[] = [];

		for (let oneProperty of currentProperties) {
			let newPath = `${path}${PATH_DELIMITER}${oneProperty}`;
			if (!this.knownPropertiesMap[newPath]) {
				additionalProperties.push(oneProperty);
			}
		}

		Logger.system.verbose(
			`getAdditionalProperties-additionalProperties for ${path}`,
			additionalProperties,
			currentProperties
		);
		return additionalProperties;
	}

	/**
	 * Get the length of an array from config
	 *
	 * @private
	 * @param {*} manifest
	 * @param {*} path
	 * @returns {number}
	 * @memberof SchemaUtil
	 */
	private getLengthOfArrayInConfig(manifest, path): number {
		Logger.system.verbose(`getLengthOfArrayInConfig for ${path}`);
		let lengthOfArrayInConfig = 0;
		let currentConfigPosition = manifest;

		let pathArgs = path.split(PATH_DELIMITER);
		for (let i = 1; i < pathArgs.length; i++) {
			let currentProperty = pathArgs[i];
			if (currentProperty.includes(ARRAY_DELIMITER)) {
				let arrayTokens = currentProperty.split(ARRAY_DELIMITER);
				let arrayName = arrayTokens[0];
				let arrayIndex = arrayTokens[1];
				currentConfigPosition = currentConfigPosition[arrayName][arrayIndex];
			} else {
				currentConfigPosition = currentConfigPosition[currentProperty];
			}

			// if config doesn't have this schema path defined, then break out of loop
			if (!currentConfigPosition) {
				break;
			}
		}

		if (currentConfigPosition && Array.isArray(currentConfigPosition)) {
			lengthOfArrayInConfig = currentConfigPosition.length;
			Logger.system.verbose(
				`getLengthOfArrayInConfig value is ${lengthOfArrayInConfig} for path=${path}`,
				currentConfigPosition
			);
		}
		return lengthOfArrayInConfig;
	}

	private processSchemaItems({ schemaObject, manifest, path, currentForceDefaults, previousPathProperty, level }) {
		if (schemaObject.type !== "array" && !Array.isArray(schemaObject.type)) {
			Logger.system.error(`recursiveWalkerForDefaults expected items of type array for ${path}`, schemaObject);
		}
		let lengthOfArrayInConfig = this.getLengthOfArrayInConfig(manifest, path);
		for (let i = 0; i < lengthOfArrayInConfig; i++) {
			let newPath = `${path}${ARRAY_DELIMITER}${i}`;
			Logger.system.debug(`processing items for ${newPath}`, schemaObject.items);
			this.recursiveWalkerForDefaults(
				manifest,
				currentForceDefaults,
				schemaObject.items,
				previousPathProperty,
				`${newPath}`,
				level
			);
		}
	}

	private processSchemaProperties({ schemaObject, manifest, path, currentForceDefaults, previousPathProperty, level }) {
		let currentProperties = Object.keys(schemaObject.properties);
		for (let thisProperty of currentProperties) {
			let thisPropertyPath = `${path}${PATH_DELIMITER}${thisProperty}`;
			// checking if thisProperty is the same as the pervious property in the path will immediate identify some circular dependencies
			if (thisProperty != previousPathProperty && typeof schemaObject.properties[thisProperty] === "object") {
				this.knownPropertiesMap[thisPropertyPath] = true;
				this.recursiveWalkerForDefaults(
					manifest,
					currentForceDefaults,
					schemaObject.properties[thisProperty],
					thisProperty,
					`${thisPropertyPath}`,
					level
				);
			}
		}
	}

	private processSchemaAdditionalProperties({
		schemaObject,
		manifest,
		path,
		currentForceDefaults,
		previousPathProperty,
		level,
	}) {
		let additionalPropertiesList = this.getAdditionalProperties(manifest, path);
		for (let i = 0; i < additionalPropertiesList.length; i++) {
			let oneAdditionalProperty = additionalPropertiesList[i];
			let newPath = `${path}${PATH_DELIMITER}${oneAdditionalProperty}`;
			Logger.system.debug(`processing additionalProperties for ${newPath}`, schemaObject.additionalProperties);
			this.recursiveWalkerForDefaults(
				manifest,
				currentForceDefaults,
				schemaObject.additionalProperties,
				previousPathProperty,
				`${newPath}`,
				level
			);
		}
	}

	/**
	 * Recursively walks the schema using it to fill in config defaults
	 *
	 * @private
	 * @param {*} manifest
	 * @param {*} currentForceDefaults
	 * @param {*} schemaObject
	 * @param {*} previousPathProperty
	 * @param {*} path
	 * @param {*} level
	 * @memberof SchemaUtil
	 */
	private recursiveWalkerForDefaults(manifest, currentForceDefaults, schemaObject, previousPathProperty, path, level) {
		Logger.system.verbose(`recursiveWalkerForDefaults: level ${level} path=${path} default=${schemaObject}`);

		// if forceDefaults is defined, then it becomes the new currentForceDefaults value for this object and its children
		if (schemaObject instanceof Object && "forceDefaults" in schemaObject) {
			currentForceDefaults = schemaObject.forceDefaults;
		}

		if (schemaObject.default && currentForceDefaults) {
			this.setDefaultForForOneProperty(manifest, path, schemaObject.default);
		}

		// limit the recursive depth because schema can have circular definitions; rather than fully identify all circular definitions can just kick out here (it's simpler and sufficient)
		if (++level >= EXCESSIVE_SCHEMA_DEPTH) {
			Logger.system.warn(
				`recursiveWalkerForDefaults stopped due to excessive schema depth although could be a valid circular dependency: level ${level} path=${path} default=${schemaObject}`
			);
		} else {
			if (schemaObject.$ref) {
				let referencedDefinition = schemaObject.$ref.replace("#/definitions/", "");
				this.recursiveWalkerForDefaults(
					manifest,
					currentForceDefaults,
					this.finsembleSchema.definitions[referencedDefinition],
					previousPathProperty,
					`${path}`,
					--level
				);
			}

			if (schemaObject.items) {
				this.processSchemaItems({ schemaObject, manifest, path, currentForceDefaults, previousPathProperty, level });
			}

			if (schemaObject.properties) {
				this.processSchemaProperties({
					schemaObject,
					manifest,
					path,
					currentForceDefaults,
					previousPathProperty,
					level,
				});
			}

			if (schemaObject.additionalProperties) {
				this.processSchemaAdditionalProperties({
					schemaObject,
					manifest,
					path,
					currentForceDefaults,
					previousPathProperty,
					level,
				});
			}
		}
		--level;
	}

	/**
	 * If not previously done, reads the schema into internal class variable for run-time use.  This function may be invoked multiple times in different contexts.
	 *
	 * @private
	 * @param {*} url the location of the schema file
	 * @returns {Promise<Boolean>} returns true if successful
	 * @memberof SchemaUtil
	 */
	private async initializeSchema(url): Promise<Boolean> {
		let successful = true;

		// first time will need to read the schema and cache it
		if (!this.finsembleSchema) {
			const { data } = await ConfigUtil.fetchConfigFile(url);
			this.finsembleSchema = data;
		}

		if (!this.finsembleSchema) {
			Logger.system.error(
				"Failure reading config schema. Confirm the schema URL configured in finsemble.configSchemaURL is correct and the file contains a valid JSON schema."
			);
			successful = false;
		}
		return successful;
	}

	/**
	 * Walks the schema using it to fill in config defaults
	 *
	 * @param {*} manifest the full manifest config including finsemble config -- default values are added to this object
	 * @memberof SchemaUtil
	 */
	public async walkSchemaAndSetConfigDefaults(manifest: any) {
		Logger.system.debug("walkSchemaAndSetConfigDefaults Starting");
		console.log("walkSchemaAndSetConfigDefaults", manifest);
		const initialForceDefaultsValue = true;

		if (await this.initializeSchema(manifest.finsemble.configSchemaURL)) {
			this.recursiveWalkerForDefaults(
				manifest,
				initialForceDefaultsValue,
				this.finsembleSchema.definitions["ManifestSubschema"],
				"ManifestSubschema",
				"",
				INITIAL_LEVEL
			);
		} else {
			Logger.system.error("Failure to initialize schema so config defaults not initialized.");
		}
		Logger.system.debug("walkSchemaAndSetConfigDefaults Done");
	}
}

export const SchemaUtilInstance = new SchemaUtil();
